import styled, { DefaultTheme, css } from 'styled-components';

import { ColorName } from 'types';

import { getHoverColor, pickColor } from 'themes/helpers/pickColor';
import { mediaHover } from 'themes/mixins/mediaHover';
import { mediaMinWidth } from 'themes/mixins/mediaQueries';

import { Button, ButtonMotives, ButtonProps } from 'components/Button';
import { Headline } from 'components/Headline';

export type ListProps = {
  motive?: ButtonMotives;
  isOpen?: boolean;
  barColor?: ColorName;
  settingsBarItem?: boolean;
};

export type ListItemProps = {
  itemMotive?: ButtonMotives;
  hasAnimation?: boolean;
  itemsHasBorder?: boolean;
};

type ButtonPropsExtended = ButtonProps & {
  barColor?: ColorName;
};

const dropdownTransitionDuration = '275ms';

const ListPrimaryCss = css<ListProps>`
  top: ${({ theme }) => theme.headerHeight};

  width: max-content;
  max-width: 114rem;
  max-height: ${({ theme }) => `calc(100vh - ${theme.headerHeight})`};
  padding: 3rem 0;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 0.8rem;
    height: 0.8rem;
  }

  &::-webkit-scrollbar-track {
    background-color: ${pickColor('grayDark')};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${pickColor('red')};
    border-radius: 0.4rem;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  background-color: ${({ barColor }) => barColor && pickColor(barColor)};
  border: 0.1rem solid ${pickColor('grayDark')};
  border-top: 0.2rem solid ${pickColor('red')};
  box-shadow: 0 1.3rem 4.2rem 1.1rem rgba(0, 0, 0, 5%);
`;

const ListSecondaryCss = css<ListProps>`
  top: 4.5rem;

  width: 100%;
  display: flex;

  border-top: 0.2rem solid ${pickColor('red')};
  border-bottom: 0.1rem solid ${pickColor('gray')};
  background-color: ${pickColor('black')};
`;

const DropdownContainer = styled.div<ListProps>`
  position: absolute;
  left: 0;

  z-index: 999;

  visibility: hidden;
  opacity: 0;
  transform: rotateX(-90deg);
  transform-origin: top center;

  transition:
    opacity ${dropdownTransitionDuration} ease-in-out,
    transform ${dropdownTransitionDuration} ease-in-out,
    visibility 0s linear ${dropdownTransitionDuration};

  ${({ isOpen }) =>
    isOpen &&
    css`
      visibility: visible;
      opacity: 1;
      transform: rotateX(0deg);
      transition:
        opacity ${dropdownTransitionDuration} ease-in-out,
        transform ${dropdownTransitionDuration} ease-in-out,
        visibility 0s linear 0s;
    `}

  ${({ motive }) => motive === 'default' && ListPrimaryCss};
  ${({ motive }) => motive === 'settings' && ListSecondaryCss};

  ${mediaMinWidth.md(css`
    position: static;

    width: 20rem;
    margin-left: 2rem;
  `)}
  background-color: ${({ barColor }) => barColor && pickColor(barColor)};
`;

const ScrollArea = styled.div`
  flex: 1;
  min-width: 0;
  max-height: 37rem;

  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ScrollTrack = styled.div`
  position: relative;
  flex-shrink: 0;

  width: 0.6rem;
  margin: 0 0.4rem;
`;

const ScrollThumb = styled.div`
  position: absolute;
  left: 0;

  width: 100%;

  background-color: ${pickColor('red')};
  border-radius: 0.3rem;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const FlexContainer = styled.div<{ $compact?: boolean; $alignLeft?: boolean }>`
  display: flex;
  justify-content: ${({ $compact, $alignLeft }) =>
    $alignLeft ? 'flex-start' : $compact ? 'center' : 'flex-start'};
`;

const List = styled.ul``;

const Trigger = styled(Button)<ButtonPropsExtended & { $wide?: boolean; $minWidth?: string }>`
  height: 100%;

  ${({ $minWidth }) => $minWidth && css`min-width: ${$minWidth};`}

  .icon-right {
    margin-left: 0.5rem;
    font-size: 1.8rem;
  }

  ${({ $wide }) =>
    $wide &&
    css`
      display: flex;
      align-items: center;

      max-width: 20rem;
      padding: 0 2.4rem 0 1.5rem;

      .icon-left {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-right: 0.4rem;
        font-size: 1.6rem;
        line-height: 1;
        position: relative;
        top: -0.2rem;
      }
    `}

  &:hover {
    background-color: ${({ barColor }) => barColor && pickColor(getHoverColor(barColor))};
  }
`;

const ListItem = styled.li<ListItemProps>`
  ${({ itemsHasBorder }) =>
    itemsHasBorder
      ? css`
          &:not(:last-of-type) {
            border-bottom: 0.1rem solid ${pickColor('whiteDark')};
          }
        `
      : null}

  ${({ hasAnimation }) =>
    hasAnimation
      ? css`
          span {
            transition: padding-left
              ${({ theme }: { theme: DefaultTheme }) => theme.transitionSpeed} ease-in-out;
          }

          ${mediaHover(css`
            span {
              padding-left: 0.6rem;
            }
          `)}
        `
      : null};

  ${mediaMinWidth.md(css`
    padding: 1.2rem 0;
  `)}
`;

const Dropdown = styled.div<{ triggerOnClick?: boolean }>`
  height: 100%;

  ${mediaMinWidth.md(css`
    position: relative;
  `)}

  ${({ triggerOnClick }) =>
    !triggerOnClick &&
    mediaHover(css`
      & > ${DropdownContainer} {
        visibility: visible;
        opacity: 1;
        transform: rotateX(0deg);
        transition:
          opacity ${dropdownTransitionDuration} ease-in-out,
          transform ${dropdownTransitionDuration} ease-in-out,
          visibility 0s linear 0s;
      }
    `)}
`;

const SubDropdown = styled.div<{ isOpen?: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};

  margin-left: 2rem;
`;

const SubTrigger = styled.div``;

const ListItemButton = styled(Button)<{
  barColor: ColorName;
  $compact?: boolean;
  $centered?: boolean;
}>`
  position: relative;

  width: 100%;
  height: 3.6rem;
  padding: 0 1.2rem;

  ${({ $centered }) => $centered && css`justify-content: center;`}

  ${({ $compact, $centered }) =>
    $compact &&
    !$centered &&
    css`
      .icon-left {
        margin-left: -0.4rem;
        font-size: 1.4rem;
      }
    `}

  &:hover {
    background-color: ${({ barColor }) => barColor && pickColor(getHoverColor(barColor))};
  }
`;

const Title = styled(Headline)`
  margin-bottom: 1.2rem;
  color: ${pickColor('white')};
`;

const EmptyMessage = styled(Headline)`
  min-width: 20rem;
  padding: 1.2rem 2rem;
  text-align: center;
  color: ${pickColor('white')};
`;

const DisabledItem = styled.span`
  display: flex;
  align-items: center;
  height: 3.6rem;
  padding: 0 1.2rem;
  font-family: 'LFT Zeno', sans-serif;
  font-weight: 200;
  font-size: 1.2rem;
  color: ${pickColor('white')};
  opacity: 0.7;
  cursor: default;
`;

const Wrapper = styled.div<{ $compact?: boolean }>`
  /* max-content instead of a fixed width: a column sizes to fit its own
     labels (however many items or however long a label it holds) instead of
     wrapping them inside a cramped fixed box — see the "T-SHIRT_POLO_ABITI"
     wrap-after-hyphen bug this replaced. min-width keeps short columns from
     looking cramped; the row-level max-width + overflow-x:auto on
     DropdownContainer is the fallback once columns stop fitting. */
  width: ${({ $compact }) => ($compact ? '100%' : 'max-content')};
  min-width: ${({ $compact }) => ($compact ? '0' : '17rem')};
  flex-shrink: 0;
  padding: ${({ $compact }) => ($compact ? '0' : '0 1.6rem')};

  &:not(:first-child) {
    padding-left: ${({ $compact }) => ($compact ? '0' : '1.6rem')};
  }

  &:not(:last-child) {
    border-right: 0.1rem solid ${pickColor('white')};
  }
`;

export {
  DisabledItem,
  Dropdown,
  DropdownContainer,
  EmptyMessage,
  FlexContainer,
  List,
  ListItem,
  ListItemButton,
  ScrollArea,
  ScrollThumb,
  ScrollTrack,
  SubDropdown,
  SubTrigger,
  Title,
  Trigger,
  Wrapper,
};
